import { Button } from "@legal-500/legal500-design-system";
import { type CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip, type TooltipProps } from "recharts";
import { dbManagerService } from "../../../api/dbManager.service.js";
import type {
  BffRedisBreakdownBucket,
  BffRedisBreakdownResponse,
  BffRedisOrphans,
  BffRedisSlotDirectoryFamily,
  BucketClassification,
} from "../../../api/bffRedisBreakdown.types.js";
import EmptyState from "../../../components/EmptyState/index.js";
import { PageHeader } from "../../../components/PageHeader/index.js";
import { UnauthorizedView } from "../../../components/UnauthorizedView/index.js";
import styles from "./RedisBreakdown.module.css";

const TOP_BUCKETS = 12;

const CLASSIFICATION_COLOURS: Record<BucketClassification, string> = {
  "registered-slot": "#2e7d32",
  "orphan-slot": "#dc3545",
  "legacy-doc": "#f59e0b",
  "sync-metadata": "#6b7280",
  "platform-control": "#3b82f6",
  autocomplete: "#8b5cf6",
  unknown: "#eab308",
};

const OTHER_COLOUR = "#9ca3af";

const CLASSIFICATION_LABEL: Record<BucketClassification, string> = {
  "registered-slot": "Registered slot",
  "orphan-slot": "Orphan slot",
  "legacy-doc": "Legacy doc",
  "sync-metadata": "Sync metadata",
  "platform-control": "Platform control",
  autocomplete: "Autocomplete",
  unknown: "Unknown",
};

type PieSlice = {
  name: string;
  keyCount: number;
  percent: number;
  classification: BucketClassification | "other";
  isOther: boolean;
  underlyingBuckets?: BffRedisBreakdownBucket[];
};

type BucketRow = BffRedisBreakdownBucket;

type SortDirection = "asc" | "desc";
type SortField = "name" | "classification" | "keyCount" | "percent";

function formatNumber(value: number): string {
  return value.toLocaleString();
}

function formatMs(value: number): string {
  if (value >= 1000) return `${(value / 1000).toFixed(1)}s`;
  return `${value} ms`;
}

function formatRelative(date: Date | null): string {
  if (!date) return "never";
  const ageMs = Date.now() - date.getTime();
  if (ageMs < 60_000) return `${Math.max(1, Math.round(ageMs / 1000))}s ago`;
  if (ageMs < 3_600_000) return `${Math.round(ageMs / 60_000)}m ago`;
  return date.toLocaleString();
}

function isOrphanPolicyDanger(
  policy: string | null,
  usedHuman: string | null,
  maxHuman: string | null,
): boolean {
  if (policy !== "noeviction") return false;
  if (!usedHuman || !maxHuman) return false;
  const used = parseHumanBytes(usedHuman);
  const max = parseHumanBytes(maxHuman);
  if (used === null || max === null || max === 0) return false;
  return used / max >= 0.85;
}

function parseHumanBytes(value: string): number | null {
  const match = /^([\d.]+)\s*([KMGT]?)B?$/i.exec(value.trim());
  if (!match) return null;
  const numericValue = Number.parseFloat(match[1]);
  if (Number.isNaN(numericValue)) return null;
  const unit = match[2].toUpperCase();
  const multipliers: Record<string, number> = {
    "": 1,
    K: 1024,
    M: 1024 ** 2,
    G: 1024 ** 3,
    T: 1024 ** 4,
  };
  return numericValue * (multipliers[unit] ?? 1);
}

function buildPieSlices(buckets: BffRedisBreakdownBucket[]): PieSlice[] {
  if (buckets.length === 0) return [];
  const sorted = [...buckets].sort((bucketA, bucketB) => bucketB.keyCount - bucketA.keyCount);
  if (sorted.length <= TOP_BUCKETS) {
    return sorted.map(bucket => ({
      name: bucket.name,
      keyCount: bucket.keyCount,
      percent: bucket.percent,
      classification: bucket.classification,
      isOther: false,
    }));
  }
  const top = sorted.slice(0, TOP_BUCKETS);
  const rest = sorted.slice(TOP_BUCKETS);
  const otherKeyCount = rest.reduce((acc, bucket) => acc + bucket.keyCount, 0);
  const otherPercent = rest.reduce((acc, bucket) => acc + bucket.percent, 0);
  return [
    ...top.map(bucket => ({
      name: bucket.name,
      keyCount: bucket.keyCount,
      percent: bucket.percent,
      classification: bucket.classification,
      isOther: false,
    })),
    {
      name: `other (${rest.length} buckets)`,
      keyCount: otherKeyCount,
      percent: otherPercent,
      classification: "other" as const,
      isOther: true,
      underlyingBuckets: rest,
    },
  ];
}

function sliceColour(slice: PieSlice): string {
  if (slice.isOther) return OTHER_COLOUR;
  return CLASSIFICATION_COLOURS[slice.classification as BucketClassification];
}

function classificationColour(classification: BucketClassification): string {
  return CLASSIFICATION_COLOURS[classification];
}

function PieTooltip({ active, payload }: TooltipProps<number, string>) {
  if (!active || !payload || payload.length === 0) return null;
  const raw = payload[0]?.payload as PieSlice | undefined;
  if (!raw) return null;
  return (
    <div className={styles.tooltipBox}>
      <div className={styles.tooltipName}>{raw.name}</div>
      <div className={styles.tooltipLine}>
        <span>Keys</span>
        <span>{formatNumber(raw.keyCount)}</span>
      </div>
      <div className={styles.tooltipLine}>
        <span>Percent</span>
        <span>{raw.percent.toFixed(2)}%</span>
      </div>
      {!raw.isOther && (
        <div className={styles.tooltipLine}>
          <span>Class</span>
          <span>{CLASSIFICATION_LABEL[raw.classification as BucketClassification]}</span>
        </div>
      )}
    </div>
  );
}

type LoadingPanelProps = {
  startedAt: number | null;
};

function LoadingPanel({ startedAt }: LoadingPanelProps) {
  const [elapsedMs, setElapsedMs] = useState(0);

  useEffect(() => {
    if (startedAt === null) return;
    setElapsedMs(Date.now() - startedAt);
    const interval = setInterval(() => {
      setElapsedMs(Date.now() - startedAt);
    }, 500);
    return () => clearInterval(interval);
  }, [startedAt]);

  return (
    <div className={styles.loadingSection}>
      <div className={styles.spinner} aria-hidden="true" />
      <p className={styles.loadingTitle}>Scanning Redis...</p>
      <p className={styles.loadingMessage}>
        The SCAN walks every key in the cluster. On a production-sized dataset (~2M keys) this
        usually completes in 30-60 seconds. Please leave this tab open.
      </p>
      <p className={styles.loadingElapsed}>
        Elapsed: {(elapsedMs / 1000).toFixed(1)}s (timeout 90s)
      </p>
    </div>
  );
}

type StatCardProps = {
  label: string;
  value: string;
  variant?: "default" | "danger";
  note?: string;
  small?: boolean;
};

function StatCard({ label, value, variant = "default", note, small = false }: StatCardProps) {
  return (
    <div className={`${styles.statCard} ${variant === "danger" ? styles.statCardDanger : ""}`}>
      <span className={styles.statLabel}>{label}</span>
      <span className={small ? styles.statValueSmall : styles.statValue}>{value}</span>
      {note && <span className={styles.statDangerNote}>{note}</span>}
    </div>
  );
}

type OrphanCalloutProps = {
  orphans: BffRedisOrphans;
};

function hasAnyOrphans(orphans: BffRedisOrphans): boolean {
  return (
    orphans.slotKeyspacesWithoutDirectoryEntry.length > 0 ||
    orphans.directoryEntriesWithoutKeys.length > 0 ||
    orphans.legacyTopLevelKeys.length > 0
  );
}

function OrphanCallout({ orphans }: OrphanCalloutProps) {
  if (!hasAnyOrphans(orphans)) return null;
  return (
    <div className={styles.orphanAlert} role="alert">
      <h2 className={styles.orphanAlertTitle}>Orphan data detected</h2>

      {orphans.slotKeyspacesWithoutDirectoryEntry.length > 0 && (
        <div className={styles.orphanGroup}>
          <h3 className={styles.orphanGroupHeading}>
            Slot keyspaces present in Redis but not registered
          </h3>
          <ul className={styles.orphanList}>
            {orphans.slotKeyspacesWithoutDirectoryEntry.map(entry => (
              <li key={`${entry.family}/${entry.keyspace}`}>
                {entry.family}/{entry.keyspace}
                <span className={styles.orphanCount}>({formatNumber(entry.keyCount)} keys)</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {orphans.directoryEntriesWithoutKeys.length > 0 && (
        <div className={styles.orphanGroup}>
          <h3 className={styles.orphanGroupHeading}>
            Slot directory entries with no keys in Redis
          </h3>
          <ul className={styles.orphanList}>
            {orphans.directoryEntriesWithoutKeys.map(entry => (
              <li key={`${entry.family}/${entry.keyspace}`}>
                {entry.family}/{entry.keyspace}
              </li>
            ))}
          </ul>
        </div>
      )}

      {orphans.legacyTopLevelKeys.length > 0 && (
        <div className={styles.orphanGroup}>
          <h3 className={styles.orphanGroupHeading}>Legacy top-level keys (pre-slot schema)</h3>
          <ul className={styles.orphanList}>
            {orphans.legacyTopLevelKeys.map(entry => (
              <li key={entry.pattern}>
                {entry.pattern}
                <span className={styles.orphanCount}>({formatNumber(entry.keyCount)} keys)</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

type BucketsTableProps = {
  buckets: BucketRow[];
};

function BucketsTable({ buckets }: BucketsTableProps) {
  const [classificationFilter, setClassificationFilter] = useState<BucketClassification | null>(
    null,
  );
  const [sortField, setSortField] = useState<SortField>("keyCount");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const handleSort = useCallback(
    (field: SortField) => {
      if (sortField === field) {
        setSortDirection(prev => (prev === "asc" ? "desc" : "asc"));
        return;
      }
      setSortField(field);
      setSortDirection(field === "name" || field === "classification" ? "asc" : "desc");
    },
    [sortField],
  );

  const visibleClassifications = useMemo(() => {
    const set = new Set<BucketClassification>();
    for (const bucket of buckets) set.add(bucket.classification);
    return Array.from(set);
  }, [buckets]);

  const filtered = useMemo(() => {
    if (!classificationFilter) return buckets;
    return buckets.filter(bucket => bucket.classification === classificationFilter);
  }, [buckets, classificationFilter]);

  const sorted = useMemo(() => {
    const rows = [...filtered];
    rows.sort((rowA, rowB) => {
      const direction = sortDirection === "asc" ? 1 : -1;
      switch (sortField) {
        case "name":
          return rowA.name.localeCompare(rowB.name) * direction;
        case "classification":
          return rowA.classification.localeCompare(rowB.classification) * direction;
        case "keyCount":
          return (rowA.keyCount - rowB.keyCount) * direction;
        case "percent":
          return (rowA.percent - rowB.percent) * direction;
        default:
          return 0;
      }
    });
    return rows;
  }, [filtered, sortField, sortDirection]);

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Buckets</h2>
        <span className={styles.sectionSubtitle}>
          {formatNumber(sorted.length)} of {formatNumber(buckets.length)} shown
        </span>
      </div>

      <div className={styles.filterRow}>
        <span className={styles.filterLabel}>Filter</span>
        <button
          type="button"
          className={`${styles.filterButton} ${classificationFilter === null ? styles.filterButtonActive : ""}`}
          onClick={() => setClassificationFilter(null)}
        >
          All
        </button>
        {visibleClassifications.map(classification => (
          <button
            type="button"
            key={classification}
            className={`${styles.filterButton} ${classificationFilter === classification ? styles.filterButtonActive : ""}`}
            onClick={() =>
              setClassificationFilter(prev => (prev === classification ? null : classification))
            }
          >
            <span
              className={styles.swatch}
              style={{ backgroundColor: classificationColour(classification) }}
              aria-hidden="true"
            />
            {CLASSIFICATION_LABEL[classification]}
          </button>
        ))}
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" }}>
        <thead>
          <tr>
            <SortableTh
              label="Bucket"
              field="name"
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
            <SortableTh
              label="Classification"
              field="classification"
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
            <SortableTh
              label="Key count"
              field="keyCount"
              align="right"
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
            <SortableTh
              label="Percent"
              field="percent"
              align="right"
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
          </tr>
        </thead>
        <tbody>
          {sorted.map(bucket => (
            <tr key={bucket.name}>
              <td className={styles.bucketName} style={cellStyle}>
                {bucket.name}
              </td>
              <td style={cellStyle}>
                <span
                  className={styles.classificationCell}
                  style={{
                    backgroundColor: `${classificationColour(bucket.classification)}1f`,
                    color: classificationColour(bucket.classification),
                  }}
                >
                  <span
                    className={styles.swatch}
                    style={{ backgroundColor: classificationColour(bucket.classification) }}
                    aria-hidden="true"
                  />
                  {CLASSIFICATION_LABEL[bucket.classification]}
                </span>
              </td>
              <td className={styles.numericCell} style={cellStyle}>
                {formatNumber(bucket.keyCount)}
              </td>
              <td className={styles.numericCell} style={cellStyle}>
                {bucket.percent.toFixed(2)}%
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

const cellStyle: CSSProperties = {
  padding: "0.5rem 0.75rem",
  borderBottom: "1px solid var(--color-border, #e5e7eb)",
  color: "var(--color-text-primary, #111827)",
  verticalAlign: "top",
};

type SortableThProps = {
  label: string;
  field: SortField;
  align?: "left" | "right";
  sortField: SortField;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
};

function SortableTh({
  label,
  field,
  align = "left",
  sortField,
  sortDirection,
  onSort,
}: SortableThProps) {
  const isActive = sortField === field;
  const arrow = isActive ? (sortDirection === "asc" ? " ▲" : " ▼") : "";
  return (
    <th
      style={{
        textAlign: align,
        padding: "0.5rem 0.75rem",
        fontSize: "0.75rem",
        fontWeight: 600,
        textTransform: "uppercase",
        color: "var(--color-text-secondary, #6b7280)",
        borderBottom: "1px solid var(--color-border, #e5e7eb)",
        cursor: "pointer",
        userSelect: "none",
        whiteSpace: "nowrap",
      }}
      onClick={() => onSort(field)}
      aria-sort={isActive ? (sortDirection === "asc" ? "ascending" : "descending") : "none"}
    >
      {label}
      {arrow}
    </th>
  );
}

type SlotDirectoryPanelProps = {
  slotDirectory: BffRedisSlotDirectoryFamily[];
};

function SlotDirectoryPanel({ slotDirectory }: SlotDirectoryPanelProps) {
  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Slot directory</h2>
        <span className={styles.sectionSubtitle}>Registered keyspaces per family</span>
      </div>
      {slotDirectory.map(family => {
        const totalKeys = family.entries.reduce((acc, entry) => acc + entry.keyCount, 0);
        const withKeys = family.entries.filter(entry => entry.hasKeys).length;
        return (
          <details key={family.family} className={styles.familyPanel}>
            <summary>
              <span>
                {family.family}{" "}
                <span className={styles.familyMeta}>
                  ({family.entries.length} slots, {withKeys} with keys, {formatNumber(totalKeys)}{" "}
                  total keys)
                </span>
              </span>
              <span aria-hidden="true">▾</span>
            </summary>
            <div className={styles.familyPanelInner}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" }}>
                <thead>
                  <tr>
                    <th
                      style={{
                        textAlign: "left",
                        padding: "0.375rem 0.625rem",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        color: "var(--color-text-secondary, #6b7280)",
                        borderBottom: "1px solid var(--color-border, #e5e7eb)",
                      }}
                    >
                      Keyspace
                    </th>
                    <th
                      style={{
                        textAlign: "center",
                        padding: "0.375rem 0.625rem",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        color: "var(--color-text-secondary, #6b7280)",
                        borderBottom: "1px solid var(--color-border, #e5e7eb)",
                        width: "100px",
                      }}
                    >
                      Has keys
                    </th>
                    <th
                      style={{
                        textAlign: "right",
                        padding: "0.375rem 0.625rem",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        color: "var(--color-text-secondary, #6b7280)",
                        borderBottom: "1px solid var(--color-border, #e5e7eb)",
                        width: "140px",
                      }}
                    >
                      Key count
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {family.entries.length === 0 && (
                    <tr>
                      <td colSpan={3} style={{ padding: "0.5rem", color: "#6b7280" }}>
                        No registered keyspaces
                      </td>
                    </tr>
                  )}
                  {family.entries.map(entry => (
                    <tr key={entry.keyspace}>
                      <td
                        className={styles.bucketName}
                        style={{
                          padding: "0.375rem 0.625rem",
                          borderBottom: "1px solid var(--color-border, #e5e7eb)",
                        }}
                      >
                        {entry.keyspace}
                      </td>
                      <td
                        style={{
                          padding: "0.375rem 0.625rem",
                          borderBottom: "1px solid var(--color-border, #e5e7eb)",
                          textAlign: "center",
                        }}
                      >
                        <span className={entry.hasKeys ? styles.hasKeysYes : styles.hasKeysNo}>
                          {entry.hasKeys ? "✓" : "✗"}
                        </span>
                      </td>
                      <td
                        className={styles.numericCell}
                        style={{
                          padding: "0.375rem 0.625rem",
                          borderBottom: "1px solid var(--color-border, #e5e7eb)",
                        }}
                      >
                        {formatNumber(entry.keyCount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        );
      })}
    </section>
  );
}

type ChartPanelProps = {
  buckets: BffRedisBreakdownBucket[];
};

function ChartPanel({ buckets }: ChartPanelProps) {
  const slices = useMemo(() => buildPieSlices(buckets), [buckets]);

  if (slices.length === 0) {
    return (
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Bucket distribution</h2>
        </div>
        <p className={styles.sectionSubtitle}>No keys found.</p>
      </section>
    );
  }

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Bucket distribution</h2>
        <span className={styles.sectionSubtitle}>
          Top {Math.min(TOP_BUCKETS, slices.length)} buckets by key count
        </span>
      </div>
      <div className={styles.chartWrapper}>
        <div className={styles.chartContainer}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={slices}
                dataKey="keyCount"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius="85%"
                innerRadius="45%"
                paddingAngle={1}
                isAnimationActive={false}
              >
                {slices.map(slice => (
                  <Cell
                    key={slice.name}
                    fill={sliceColour(slice)}
                    stroke="#ffffff"
                    strokeWidth={1}
                  />
                ))}
              </Pie>
              <Tooltip content={<PieTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className={styles.legend}>
          {slices.map(slice => (
            <div key={slice.name} className={styles.legendItem}>
              <span
                className={styles.legendSwatch}
                style={{ backgroundColor: sliceColour(slice) }}
                aria-hidden="true"
              />
              <span className={styles.legendName}>{slice.name}</span>
              <span className={styles.legendCount}>
                {formatNumber(slice.keyCount)} · {slice.percent.toFixed(1)}%
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function RedisBreakdownPage() {
  const [data, setData] = useState<BffRedisBreakdownResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchStartedAt, setFetchStartedAt] = useState<number | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const fetchBreakdown = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const startedAt = Date.now();
    setFetchStartedAt(startedAt);
    setLoading(true);
    setError(null);
    try {
      const response = await dbManagerService.getBffRedisBreakdown(controller.signal);
      if (controller.signal.aborted) return;
      setData(response);
      setLastFetchedAt(new Date());
    } catch (err) {
      if (controller.signal.aborted) return;
      const safeError =
        err instanceof Error ? err : new Error(typeof err === "string" ? err : "Unknown error");
      setError(safeError);
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
        setFetchStartedAt(null);
      }
    }
  }, []);

  useEffect(() => {
    void fetchBreakdown();
    return () => {
      abortRef.current?.abort();
    };
  }, [fetchBreakdown]);

  const status = (error as (Error & { status?: number }) | null)?.status;
  if (status === 401 || status === 403) {
    return <UnauthorizedView />;
  }

  const policyDanger = data
    ? isOrphanPolicyDanger(
        data.redisInfo.maxmemoryPolicy,
        data.redisInfo.usedMemoryHuman,
        data.redisInfo.maxmemoryHuman,
      )
    : false;

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title="BFF Redis Breakdown"
        subtitle="Memory usage, key distribution, and orphan detection for the BFF Redis cluster."
      />

      <div className={styles.toolbar}>
        <span className={styles.toolbarMeta}>
          Last fetched: {formatRelative(lastFetchedAt)}
          {data && ` · scan ${formatMs(data.redisInfo.scanDurationMs)}`}
        </span>
        <Button
          variant="contained"
          color="primary"
          onClick={() => void fetchBreakdown()}
          disabled={loading}
        >
          {loading ? "Scanning..." : "Refresh"}
        </Button>
      </div>

      {loading && !data && <LoadingPanel startedAt={fetchStartedAt} />}

      {error && !loading && (
        <EmptyState
          variant="error"
          title="Failed to load Redis breakdown"
          message={error.message}
          action={{ label: "Retry", onClick: () => void fetchBreakdown() }}
        />
      )}

      {data && (
        <>
          <div className={styles.statsRow}>
            <StatCard label="Used memory" value={data.redisInfo.usedMemoryHuman ?? "unknown"} />
            <StatCard label="Max memory" value={data.redisInfo.maxmemoryHuman ?? "unknown"} />
            <StatCard
              label="Maxmemory policy"
              value={data.redisInfo.maxmemoryPolicy ?? "unknown"}
              variant={policyDanger ? "danger" : "default"}
              small
              note={policyDanger ? "Near max usage with noeviction" : undefined}
            />
            <StatCard label="Total keys" value={formatNumber(data.redisInfo.totalKeys)} />
            <StatCard label="Scan duration" value={formatMs(data.redisInfo.scanDurationMs)} small />
          </div>

          <OrphanCallout orphans={data.orphans} />

          <ChartPanel buckets={data.buckets} />

          <BucketsTable buckets={data.buckets} />

          <SlotDirectoryPanel slotDirectory={data.slotDirectory} />
        </>
      )}

      {loading && data && (
        <p className={styles.toolbarMeta}>Refreshing... (existing data shown above)</p>
      )}
    </div>
  );
}

export default RedisBreakdownPage;
